import * as yup from 'yup';
import { error } from '../text';
import { ItemListType, PatternType } from '../../types/pattern';
import { type ItemValidationType } from '../../types/common';
import { toFormFieldName } from '../../utils/pattern/formFieldName';
import {
  itemValidationResultMessage,
  supportsLengthConstraint,
  supportsValueRangeConstraint,
  supportsCharTypeConstraint,
  supportsPatternConstraint,
  supportsDateRangeConstraint,
  isChoiceDataType,
} from '../../constants/itemValidation';
import {
  checkDateRange,
  dateRangeMessage,
  matchesCharType,
  matchesPattern,
  type DateRangeRule,
} from '../../utils/pattern/itemValidationRules';

const allowedFileTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
const maxFileSize = 10 * 1024 * 1024; // 10MB
const maxFiles = process.env.REACT_APP_PATTERN_MAX_FILES;

const imageValidation = yup
  .mixed<File[]>()
  .optional()
  .test({
    name: 'fileValidation',
    test: function (files) {
      if (!Array.isArray(files) || files.length === 0) return true;

      const errors: string[] = [];

      if (!files.every(file => allowedFileTypes.includes(file.type))) {
        errors.push(error.invalidFileType);
      }
      if (!files.every(file => file.size <= maxFileSize)) {
        errors.push(error.maxSize);
      }
      if (files.length > Number(maxFiles)) {
        errors.push(`${maxFiles}ファイルまでアップロード可能です`);
      }

      if (errors.length > 0) {
        return this.createError({ message: errors.join('\n') });
      }
      return true;
    },
  });

export const patternRegisterSchema = yup.object().shape({
  title: yup
    .string()
    .required(error.required)
    .max(18, '読取パターン名は18文字以内で入力してください'),
  documentName: yup.string().nullable().max(100, '書類の名称は100文字以内で入力してください'),
  description: yup.string().nullable().max(2000, '書類の説明は2000文字以内で入力してください'),
  remarks: yup.string().nullable().max(2000, error.below2000),
  fileType: yup.number().required(error.required),
  images: imageValidation,
  itemList: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(), // 親項目ID（形式: "1", "2", ...）
        parentValue: yup.string().required(error.required).max(250, error.below250),
        type: yup.string().required(error.required).notOneOf(['__placeholder__'], error.required),
        children: yup
          .array()
          .of(
            yup.object().shape({
              id: yup.string().required(), // 子項目ID（形式: "2-1", "2-2", ...）
              childValue: yup.string().required(error.required).max(250, error.below250),
              type: yup
                .string()
                .required(error.required)
                .notOneOf(['__placeholder__'], error.required),
              displayWidth: yup.number().required(error.required).typeError(error.required),
            }),
          )
          .default([]), // nullではなく空配列をデフォルトに
      }),
    )
    .required('項目は最低1つ必要です'),
});

// 文字列が空（null / undefined / 空白のみ）かどうか
const isEmptyValue = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

// 項目のデータ型・バリデーション制約（#2815）から yup スキーマを生成する共通関数。
// 親項目・子項目で共通利用する。
const buildFieldSchema = (
  type: string | undefined,
  validation: ItemValidationType,
  /**
   * 日付範囲（#3357）の基準日。結果ドキュメントの created_at（OCR を実行した日）を渡す。
   * 閲覧日（new Date()）を使うと、要注意（サーバで判定・保存）と画面表示で判定が
   * 食い違うため、呼び出し側から明示的に渡す
   */
  dateRangeBaseDate?: Date,
): yup.AnySchema => {
  const {
    required,
    minLength,
    maxLength,
    minValue,
    maxValue,
    treatZeroAsEmpty,
    charType,
    pattern,
    patternMessage,
  } = validation;

  if (type === 'number') {
    let schema = yup
      .number()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string' && originalValue.trim() === '') {
          return null;
        }
        // カンマ付き数値を許容（カンマ除去してから数値変換）
        if (typeof originalValue === 'string') {
          const stripped = originalValue.replace(/,/g, '');
          if (stripped !== '' && !/^-?\d*\.?\d*$/.test(stripped)) {
            return NaN;
          }
          const num = parseFloat(stripped);
          return isNaN(num) ? value : num;
        }
        return Number(value);
      })
      .typeError('数値を入力してください')
      .nullable();
    // 数値範囲制約（#2815）
    if (supportsValueRangeConstraint(type)) {
      if (minValue !== undefined) {
        schema = schema.min(minValue, itemValidationResultMessage.minValue(minValue));
      }
      if (maxValue !== undefined) {
        schema = schema.max(maxValue, itemValidationResultMessage.maxValue(maxValue));
      }
    }
    if (required) {
      schema = schema.test(
        'required',
        itemValidationResultMessage.required,
        // 「0 を空欄として扱う」（#3262）が ON の項目では 0 も未入力とみなす。
        // 要注意判定（utils/pattern/reviewIssues.ts の isEmptyValue）と揃える。
        // 揃っていないと、要注意は出るのに画面には何も出ない状態になる（#3309）
        value =>
          value !== null && value !== undefined && !(treatZeroAsEmpty === true && value === 0),
      );
    }
    return schema;
  }

  if (type === 'date') {
    let schema = yup
      .mixed()
      .transform((_, originalValue) => {
        // 空文字列、null、undefinedの場合はnullを返す
        if (isEmptyValue(originalValue)) {
          return null;
        }
        // 文字列の場合はDateオブジェクトに変換を試行
        if (typeof originalValue === 'string') {
          const dateValue = new Date(originalValue);
          return isNaN(dateValue.getTime()) ? originalValue : dateValue;
        }
        // Dateオブジェクトの場合はそのまま返す
        if (originalValue instanceof Date) {
          return originalValue;
        }
        return originalValue;
      })
      .test('isValidDate', '有効な日付を入力してください', function (value) {
        if (value === null || value === undefined) {
          return true; // nullは許可
        }
        if (value instanceof Date) {
          return !isNaN(value.getTime()); // 有効なDateオブジェクトかチェック
        }
        return false; // Date以外は無効
      })
      .nullable();
    // 日付範囲（#3357）。基準日が渡されていないときは判定しない
    if (supportsDateRangeConstraint(type) && dateRangeBaseDate !== undefined) {
      const dateRule: DateRangeRule = {
        noPast: validation.dateNoPast,
        noFuture: validation.dateNoFuture,
        pastLimit: validation.datePastLimit,
        pastUnit: validation.datePastUnit,
        futureLimit: validation.dateFutureLimit,
        futureUnit: validation.dateFutureUnit,
      };
      schema = schema.test('dateRange', '', function (value) {
        const violation = checkDateRange(value, dateRule, dateRangeBaseDate);
        if (violation === null) return true;
        return this.createError({ message: dateRangeMessage(violation, dateRule) });
      });
    }
    if (required) {
      schema = schema.test(
        'required',
        itemValidationResultMessage.required,
        value => value !== null && value !== undefined,
      );
    }
    return schema;
  }

  // 選択肢型（#3075）。値は選択肢の中から選ばれるため文字数制約は設けない。
  // 候補外の値はOCR後処理で空になり、既存データの候補外値は消さずに表示するため、
  // ここで候補に含まれるかの検証はしない（必須チェックのみ行う）
  if (isChoiceDataType(type)) {
    let schema = yup.string().nullable();
    if (required) {
      schema = schema.test(
        'required',
        itemValidationResultMessage.required,
        value => !isEmptyValue(value),
      );
    }
    return schema;
  }

  // string / textarea
  const defaultMax = type === 'textarea' ? 5000 : 250;
  const defaultMaxMessage =
    type === 'textarea' ? '5000文字以内で入力してください' : '250文字以内で入力してください';
  let schema = yup.string().nullable();
  // 文字数制約（#2815）。最大文字数は制約未指定なら従来のデフォルト上限を適用
  if (supportsLengthConstraint(type) && minLength !== undefined) {
    // yup の .min() は空文字列にもエラーを出すため、最小文字数を設定しただけで
    // 必須項目のように振る舞ってしまっていた（#3309）。
    // 空値は required の領分なので、値があるときだけ文字数を見る。
    // 要注意判定（reviewIssues）も空値では文字数制約を評価しない
    schema = schema.test(
      'minLength',
      itemValidationResultMessage.minLength(minLength),
      value => isEmptyValue(value) || String(value).length >= minLength,
    );
  }
  if (supportsLengthConstraint(type) && maxLength !== undefined) {
    schema = schema.max(maxLength, itemValidationResultMessage.maxLength(maxLength));
  } else {
    schema = schema.max(defaultMax, defaultMaxMessage);
  }
  // 文字種（#3357）。空値は required の領分なので、値があるときだけ見る
  if (supportsCharTypeConstraint(type) && charType !== undefined) {
    schema = schema.test('charType', itemValidationResultMessage.charType(charType), value =>
      matchesCharType(value, charType),
    );
  }
  // 入力パターン（#3357）。設定者がエラー文言を書いていればそちらを表示する
  if (supportsPatternConstraint(type) && pattern !== undefined && pattern !== '') {
    schema = schema.test(
      'pattern',
      patternMessage !== undefined && patternMessage !== ''
        ? patternMessage
        : itemValidationResultMessage.pattern,
      value => matchesPattern(value, pattern),
    );
  }
  if (required) {
    schema = schema.test(
      'required',
      itemValidationResultMessage.required,
      value => !isEmptyValue(value),
    );
  }
  return schema;
};

// 読取パターン結果画面用の動的バリデーションスキーマを作成する関数
export const createPatternResultSchema = (
  patternData: PatternType | null,
  /**
   * 日付範囲（#3357）の基準日。結果ドキュメントの created_at（OCR を実行した日）を渡す。
   * 未指定なら日付範囲は判定しない（パターン作成時のプレビューなど結果が無い場面）
   */
  dateRangeBaseDate?: Date,
): yup.ObjectSchema<Record<string, unknown>> => {
  const schemaFields: Record<string, yup.AnySchema> = {};

  if (patternData?.item_list) {
    patternData.item_list.forEach((item: ItemListType) => {
      // 明細（array）項目とその子項目は dynamicFormData + validateArrayField で行ごとに
      // 別管理するため、react-hook-form のフラットスキーマには登録しない。
      // 登録すると、明細の行データはフラットキー（例: 商品コード）に入らず常に空となり、
      // 必須項目が誤って「必須項目です」エラーになってしまう。
      if (item.type === 'array' || (item.children && item.children.length > 0)) {
        return;
      }

      // 単一値項目のみ react-hook-form のスキーマに登録する。
      // キーは画面のフォームと同じ変換を通す（#3309）。
      // 項目名をそのまま使うと「納入先ID」のように大文字を含む項目で
      // フォーム側のキー（納入先id）とズレ、制約が一切評価されなくなる
      schemaFields[toFormFieldName(item.parentValue)] = buildFieldSchema(
        item.type,
        item,
        dateRangeBaseDate,
      );
    });
  }

  return yup.object().shape(schemaFields);
};
